<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import Preview from "./Preview.vue";

const DEFAULT_PLACEHOLDER_URL = remoteImageStaticUrl("143850_5000_000C_030_G2023_SD_F_24252.jpg", {
  fit: "fill",
  fill: "solid",
  "trim-color": "FFFFFF",
  trim: "color",
  "trim-tol": 8,
  w: 307,
  h: 480,
  q: 80,
  dpr: 2
});

const store = useStore();
const checkoutCompositions = computed(() =>
  Object.values(store.getters["checkout/compositions"])
    .reduce((acc, comp) => {
      if (!acc.find(c => c.id === comp.id)) {
        acc.push(comp);
      }
      return acc;
    }, [])
    .sort((a, b) => a.id - b.id)
);
const allProducts = computed(() => store.getters["product/allProducts"]);
const allCompositions = computed(() =>
  allProducts.value
    .map(product =>
      product.compositions.map(comp => ({
        productId: product.id,
        id: comp.id,
        imageUrl: comp.imageUrl,
        name: product.name,
        brandAndColor: `${product.brandAndCatalogNumber} ${comp.colorName}`
      }))
    )
    .flat()
    .reduce((a, v) => ({ ...a, [v.id]: v }), {})
);
const isSingleProductPage = computed(() => store.getters["product/isSingleProductPage"]);
const selectedComposition = computed(() => store.getters["product/selectedComposition"]);
const placeholderUrl = computed(() => {
  if (isSingleProductPage.value) {
    return selectedComposition.value?.imageUrl || DEFAULT_PLACEHOLDER_URL;
  }
  return DEFAULT_PLACEHOLDER_URL;
});
</script>

<template>
  <div class="artwork-preview" data-testid="artwork-preview-list">
    <div class="artwork-preview__title">Your creation</div>
    <div class="artwork-preview__items">
      <template v-if="allProducts.length">
        <div
          v-for="comp in checkoutCompositions"
          :key="comp.id"
          class="artwork-preview__item"
          data-testid="artwork-preview-item"
        >
          <div class="artwork-preview__item-head">
            <div class="artwork-preview__item-title" data-testid="artwork-preview-item-title">
              {{ allCompositions[comp.id].name }}
            </div>
            <div class="artwork-preview__item-number" data-testid="artwork-preview-item-brand">
              {{ allCompositions[comp.id].brandAndColor }}
            </div>
            <Preview
              class="artwork-preview__preview"
              data-testid="artwork-preview-item-image"
              :composition-id="comp.id"
              :product-id="allCompositions[comp.id].productId"
              :default-image-url="allCompositions[comp.id].imageUrl"
              :preview="comp"
            />
          </div>
        </div>
      </template>

      <div v-if="checkoutCompositions.length === 0" class="artwork-preview__item">
        <div class="artwork-preview__item-title">
          Please upload your artwork to see the creation
        </div>
        <img
          alt="No artwork"
          class="artwork-preview__preview artwork-preview__preview--empty"
          :src="placeholderUrl"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.artwork-preview {
  @include with-desktop {
    width: 100%;
    border-radius: 8px;
    border: 1px solid $pod-gray-modern-200;
    background: white;
    box-shadow: $pod-card-shadow;
    padding: 20px 0 0;
    overflow: hidden;
  }

  &__title {
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 16px;

    @include with-desktop {
      text-align: center;
    }
  }

  &__preview {
    &--empty {
      margin-top: 16px;
    }

    @include with-desktop {
      height: $canvas-image-preview-height;
      object-fit: cover;
    }
  }

  &__items {
    @include with-desktop {
      overflow-y: hidden;
      overflow-x: scroll;
      white-space: nowrap;
      text-align: center;
    }
  }

  &__item-head {
    text-align: center;
    font-weight: 500;
  }

  &__item-title {
    font-size: 13px;
    margin-bottom: 1px;
  }

  &__item-number {
    font-size: 11px;
    color: $pod-gray-modern-500;
  }

  &__item {
    & + & {
      margin-top: 54px;
    }

    @include with-desktop {
      display: inline-block;

      & + & {
        margin: 0 0 0 16px;
      }
    }
  }
}
</style>
